
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeAuth, getUserRole } from '../../services/auth';
import { getWallet, fundWallet, requestPayout, subscribeToWalletTransactions, getBanks, getWithdrawalPinHash, setWithdrawalPin, verifyWithdrawalPin } from '../../services/wallet';
import { auth, db } from '../../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { toUserMessage } from '../../utils/errors';

// Prefer same-origin API; fallback to env or localhost in dev
const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '') ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'http://localhost:5555';

interface Transaction {
  id: string;
  type: 'credit' | 'debit' | 'escrow_hold' | 'escrow_release' | 'platform_fee' | 'payout_request';
  amount: number;
  description: string;
  date?: string;
  status: 'completed' | 'pending' | 'held' | 'failed';
  orderId?: string;
  reference?: string;
  method?: string;
}

interface WalletData {
  balance: number;
  escrowBalance: number;
  totalEarnings: number;
  transactions: Transaction[];
}

const WalletPage: React.FC = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<'buyer' | 'seller'>('buyer');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showFundModal, setShowFundModal] = useState(false);
const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedPaymentMethod] = useState<'paystack'>('paystack');
  // Withdrawal bank details
  const [withdrawBankAccountNumber, setWithdrawBankAccountNumber] = useState('');
  const [withdrawBankCode, setWithdrawBankCode] = useState('');
  const [resolvedAccountName, setResolvedAccountName] = useState('');
  const [isResolvingAccount, setIsResolvingAccount] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [banks, setBanks] = useState<{ code: string; name: string }[]>([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [banksError, setBanksError] = useState<string | null>(null);
  const [bankSearch, setBankSearch] = useState('');
  const [showBankSuggestions, setShowBankSuggestions] = useState(false);
  const filteredBanks = useMemo(() => {
    const q = bankSearch.trim().toLowerCase();
    if (!q) return banks;
    return banks.filter(b => b.name.toLowerCase().includes(q) || b.code.includes(q));
  }, [banks, bankSearch]);
  const [fundError, setFundError] = useState<string | null>(null);
  const [showDepositSuccess, setShowDepositSuccess] = useState(false);
  const [depositSuccessAmount, setDepositSuccessAmount] = useState(0);
  const [showWithdrawSuccess, setShowWithdrawSuccess] = useState(false);
  const [withdrawSuccessAmount, setWithdrawSuccessAmount] = useState(0);
  const [showDepositFailed, setShowDepositFailed] = useState(false);
  const [depositFailedReason, setDepositFailedReason] = useState('');
  const [showWithdrawFailed, setShowWithdrawFailed] = useState(false);
  const [withdrawFailedReason, setWithdrawFailedReason] = useState('');
  const [withdrawFailedRaw, setWithdrawFailedRaw] = useState('');
  const [showWithdrawTechDetails, setShowWithdrawTechDetails] = useState(false);
  const [showPinSetupModal, setShowPinSetupModal] = useState(false);
  const [showPinEnterModal, setShowPinEnterModal] = useState(false);
  const [pinSetup, setPinSetup] = useState('');
  const [pinSetupConfirm, setPinSetupConfirm] = useState('');
  const [pinEntry, setPinEntry] = useState('');
  const [pinStatus, setPinStatus] = useState<string | null>(null);

  // Load Paystack bank list when withdraw modal opens
  useEffect(() => {
    if (!showWithdrawModal) return;
    setBanksError(null);
    setBanksLoading(true);
    getBanks()
      .then((list) => setBanks(list))
      .catch(() => setBanksError('Failed to load banks list.'))
      .finally(() => setBanksLoading(false));
  }, [showWithdrawModal]);

  // Auto-resolve account name when account number (10 digits) + bank code are set
  useEffect(() => {
    const acct = withdrawBankAccountNumber.trim();
    const code = withdrawBankCode.trim();
    if (acct.length !== 10 || !code) {
      setResolvedAccountName('');
      setResolveError(null);
      return;
    }
    setIsResolvingAccount(true);
    setResolveError(null);
    setResolvedAccountName('');
    fetch(`${API_BASE}/api/paystack/resolve-account?account_number=${encodeURIComponent(acct)}&bank_code=${encodeURIComponent(code)}`)
      .then(r => r.json())
      .then(data => {
        const name = data?.data?.account_name;
        if (name) {
          setResolvedAccountName(name);
        } else {
          setResolveError(data?.message || 'Could not resolve account. Check the number and bank.');
        }
      })
      .catch(() => setResolveError('Network error resolving account.'))
      .finally(() => setIsResolvingAccount(false));
  }, [withdrawBankAccountNumber, withdrawBankCode]);
  
  const [walletData, setWalletData] = useState<WalletData>({
    balance: 0,
    escrowBalance: 0,
    totalEarnings: 0,
    transactions: []
  });

  const monthlyEarnings = useMemo(() => {
    try {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return (walletData.transactions || [])
        .filter(tx => tx.type === 'escrow_release')
        .filter(tx => {
          const ts: any = (tx as any).createdAt;
          const d: Date | null = toDateSafe(ts) || (tx.date ? new Date(tx.date) : null);
          return d && d >= start && d < end;
        })
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);
    } catch { return 0; }
  }, [walletData.transactions]);

  useEffect(() => {
    const unsubAuth = subscribeAuth(async (user) => {
      if (!user) {
        setIsLoggedIn(false);
        return;
      }
      setIsLoggedIn(true);
      const { role, status } = await getUserRole(user.uid);
      setUserType(role || 'buyer');
      // Handle pending status
      if (role === 'seller' && status === 'pending') {
        navigate('/pending-verification');
      }
      const data = await getWallet(user.uid);
      setWalletData(prev => ({
        ...prev,
        balance: data.balance,
        escrowBalance: data.escrowBalance,
        totalEarnings: data.totalEarnings,
      }));

      const unsubTransactions = subscribeToWalletTransactions(user.uid, (transactions) => {
        setWalletData(prev => ({
          ...prev,
          transactions: transactions.map(tx => ({
            ...tx,
            id: tx.id ?? '',
            date: (() => { const d = toDateSafe((tx as any).createdAt) || (tx.date ? new Date(tx.date) : null); return d ? d.toLocaleString() : 'N/A'; })()
          }))
        }));
      });

      const unsubWallet = onSnapshot(doc(db, 'wallets', user.uid), (snap) => {
        if (snap.exists()) {
          const w = snap.data() as any;
          setWalletData(prev => ({
            ...prev,
            balance: w.balance || 0,
            escrowBalance: w.escrowBalance || 0,
            totalEarnings: w.totalEarnings || 0,
          }));
        }
      });

      return () => { unsubTransactions(); unsubWallet(); };
    });
    return () => unsubAuth();
  }, []);



  const handleFundWallet = async () => {
    if (!fundAmount || parseFloat(fundAmount) < 100) {
      alert('Minimum funding amount is ₦100');
      return;
    }
    initializePaystackPayment();
  };

  const initializePaystackPayment = () => {
    const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string;
    if (!paystackKey) {
      alert('Paystack is not configured. Please use Flutterwave.');
      return;
    }
    const user = auth.currentUser;
    if (!user?.email) {
      alert('You must be signed in with an email to use Paystack.');
      return;
    }
    const amount = parseFloat(fundAmount); // amount the user wants credited to their wallet
    const grossCharge = paystackChargeForNaira(amount); // what we charge (Paystack fee added on top)
    const ref = `CM-WALLET-PS-${Date.now()}-${user.uid}`;
    const handler = (window as any).PaystackPop.setup({
      key: paystackKey,
      email: user.email,
      amount: Math.round(grossCharge * 100), // kobo — includes Paystack fee so the platform nets `amount`
      currency: 'NGN',
      ref,
      callback: function(response: any) {
        (async () => {
          try {
            const reference = response.reference || ref;
            const verifyResp = await fetch(`${API_BASE}/api/paystack-verify?reference=${encodeURIComponent(reference)}`);
            const data = await verifyResp.json();
            const txData = data?.data;
            if (!verifyResp.ok) {
              setShowFundModal(false);
              setDepositFailedReason(`We could not verify your payment. Your money is safe — contact support with this reference: ${reference}`);
              setShowDepositFailed(true);
              return;
            }
            if (txData?.status !== 'success') {
              setShowFundModal(false);
              setDepositFailedReason(`Your payment was not completed on Paystack's end (status: ${txData?.status}). If money was deducted, contact support with ref: ${reference}`);
              setShowDepositFailed(true);
              return;
            }
            // Credit the full amount the user intended to fund. The Paystack fee was
            // charged on top (grossCharge), so the platform still nets this full amount.
            // Guard against tampering: never credit more than was actually paid.
            const paid = Number(txData?.amount) / 100;
            const creditAmount = paid >= amount ? amount : (paid > 0 ? paid : amount);
            await fundWallet(user.uid, creditAmount, 'paystack', reference);
            try {
              const refreshed = await getWallet(user.uid);
              setWalletData(prev => ({
                ...prev,
                balance: refreshed.balance || 0,
                escrowBalance: refreshed.escrowBalance || 0,
                totalEarnings: refreshed.totalEarnings || 0,
              }));
            } catch {}
            setShowFundModal(false);
            setFundAmount('');
            setFundError(null);
            setDepositSuccessAmount(creditAmount);
            setShowDepositSuccess(true);
          } catch (e: any) {
            setShowFundModal(false);
            setDepositFailedReason(friendlyDepositError(toUserMessage(e)));
            setShowDepositFailed(true);
          }
        })();
      },
      onClose: function() { /* user closed */ },
    });
    handler.openIframe();
  };


  const handleWithdraw = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) { alert('Enter a valid amount'); return; }
    if (amount < 1000) { alert('Minimum withdrawal is ₦1,000'); return; }
    if (amount > (walletData.balance || 0)) { alert('Amount exceeds available balance'); return; }
    const accountNumber = withdrawBankAccountNumber.trim();
    const bankCode = withdrawBankCode.trim();
    const accountName = resolvedAccountName.trim();
    if (!accountNumber || accountNumber.length !== 10) { alert('Enter a valid 10-digit account number'); return; }
    if (!bankCode) { alert('Select your bank'); return; }
    if (!accountName) { alert('Account name not resolved yet. Please wait or recheck your account number and bank.'); return; }

    try {
      const hasPin = !!(await getWithdrawalPinHash(user.uid));
      if (!hasPin) {
        setShowPinSetupModal(true);
        setPinStatus(null);
        return;
      }
      setShowPinEnterModal(true);
      setPinStatus(null);
    } catch (e: any) {
      setShowWithdrawModal(false);
      const raw = String(e?.message || toUserMessage(e) || 'Unknown error');
      setWithdrawFailedReason(friendlyWithdrawError(raw));
      setWithdrawFailedRaw(raw);
      setShowWithdrawTechDetails(false);
      setShowWithdrawFailed(true);
    }
  };

  const proceedWithdraw = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const amount = parseFloat(withdrawAmount);
    const accountNumber = withdrawBankAccountNumber.trim();
    const bankCode = withdrawBankCode.trim();
    const accountName = resolvedAccountName.trim();
    try {
      await requestPayout(user.uid, amount, { accountNumber, bankCode, accountName });
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setWithdrawBankAccountNumber('');
      setWithdrawBankCode('');
      setResolvedAccountName('');
      setBankSearch('');
      setWithdrawSuccessAmount(amount);
      setShowWithdrawSuccess(true);
    } catch (e: any) {
      setShowWithdrawModal(false);
      const raw = String(e?.message || toUserMessage(e) || 'Unknown error');
      setWithdrawFailedReason(friendlyWithdrawError(raw));
      setWithdrawFailedRaw(raw);
      setShowWithdrawTechDetails(false);
      setShowWithdrawFailed(true);
    }
  };

  const submitPinSetup = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const p1 = pinSetup.trim();
    const p2 = pinSetupConfirm.trim();
    if (!/^\d{6}$/.test(p1)) { setPinStatus('PIN must be 6 digits'); return; }
    if (p1 !== p2) { setPinStatus('PINs do not match'); return; }
    try {
      await setWithdrawalPin(user.uid, p1);
      setShowPinSetupModal(false);
      setPinSetup(''); setPinSetupConfirm('');
      setShowPinEnterModal(true);
      setPinStatus('PIN set successfully. Please enter to confirm withdrawal.');
    } catch (e: any) {
      setPinStatus(toUserMessage(e));
    }
  };

  const submitPinEnter = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const p = pinEntry.trim();
    if (!/^\d{6}$/.test(p)) { setPinStatus('Enter a valid 6-digit PIN'); return; }
    try {
      const ok = await verifyWithdrawalPin(user.uid, p);
      if (!ok) { setPinStatus('Incorrect PIN'); return; }
      setShowPinEnterModal(false);
      setPinEntry('');
      await proceedWithdraw();
    } catch (e: any) {
      setPinStatus(toUserMessage(e));
    }
  };

  const friendlyDepositError = (raw: string): string => {
    const r = raw.toLowerCase();
    if (r.includes('network') || r.includes('fetch') || r.includes('failed to fetch')) return 'Network error. Check your internet connection and try again.';
    if (r.includes('secret') || r.includes('misconfiguration') || r.includes('500')) return 'A server error occurred. Your money was NOT deducted — please try again or contact support.';
    if (r.includes('already') || r.includes('duplicate')) return 'This payment has already been processed. Check your balance — it may already be credited.';
    if (r.includes('verification failed') || r.includes('not confirmed')) return 'We could not confirm your payment. If money was deducted, contact support with your payment reference.';
    return raw || 'Something went wrong with your deposit. If money was deducted, contact support.';
  };

  const friendlyWithdrawError = (raw: string): string => {
    const r = raw.toLowerCase();
    if (r.includes('insufficient') || r.includes('balance')) return 'You do not have enough balance to withdraw that amount. Check your available balance and try again.';
    if (r.includes('in progress') || r.includes('please wait')) return 'A withdrawal is already in progress. Please wait a moment before trying again.';
    if (r.includes('account') && r.includes('name')) return 'The account name does not match your profile. Make sure you are withdrawing to your own bank account.';
    if (r.includes('invalid') && r.includes('account')) return 'The account number is invalid. Double-check it and try again.';
    if (r.includes('network') || r.includes('fetch')) return 'Network error. Check your internet connection and try again.';
    if (r.includes('recipient')) return 'We could not register your bank account for transfer. Check your account number and bank, then try again.';
    if (r.includes('500') || r.includes('server')) return 'A server error occurred. Your balance has NOT been deducted — please try again.';
    if (r.includes('third') || r.includes('payout') || r.includes('initiat')) return 'Withdrawals are temporarily unavailable while we complete our payment provider setup. Your balance has been refunded. Please try again later or contact support.';
    if (r.includes('don') && r.includes('enough')) return 'Withdrawals are temporarily unavailable. Your wallet balance has been refunded. Please try again later or contact support.';
    return raw || 'Something went wrong with your withdrawal. Your balance has been refunded.';
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-wallet-line text-2xl text-red-600"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">Please log in to access your wallet</p>
          <button
            onClick={() => navigate('/sell')}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <i className="ri-arrow-left-line text-xl mr-2"></i>
              Back
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              {userType === 'buyer' ? 'Buyer Wallet' : 'Seller Wallet'}
            </h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Warning Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <i className="ri-warning-line text-yellow-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <h3 className="font-semibold text-yellow-800 mb-1">Important Notice</h3>
              <p className="text-yellow-700 text-sm">
                Any transactions outside Campus Market platform won't be accounted for if there's any scam. 
                Our sellers are legit and trustworthy within our platform's escrow protection system.
              </p>
            </div>
          </div>
        </div>

        {/* Wallet Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Available Balance</p>
                <p className="text-2xl font-bold text-gray-900">₦{walletData.balance.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-wallet-line text-green-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Escrow Balance</p>
                <p className="text-2xl font-bold text-orange-600">₦{walletData.escrowBalance.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-orange-600 text-xl"></i>
              </div>
            </div>
          </div>

          {userType === 'seller' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-blue-600">₦{walletData.totalEarnings.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-blue-600 text-xl"></i>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {userType === 'buyer' && (
            <>
              <button
                onClick={() => setShowFundModal(true)}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <i className="ri-add-line text-xl mr-2"></i>
                Fund Wallet
              </button>
              <button
                onClick={() => setShowWithdrawModal(true)}
                className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
              >
                <i className="ri-bank-line text-xl mr-2"></i>
                Withdraw Funds
              </button>
            </>
          )}
          
          {userType === 'seller' && (
            <>
              <button
                onClick={() => setShowFundModal(true)}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <i className="ri-add-line text-xl mr-2"></i>
                Fund Wallet
              </button>
              <button
                onClick={() => setShowWithdrawModal(true)}
                className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
              >
                <i className="ri-bank-line text-xl mr-2"></i>
                Withdraw Funds
              </button>
            </>
          )}
        </div>

        {/* Monthly Earnings (Seller) */}
        {userType === 'seller' && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Earnings This Month</p>
                <p className="text-2xl font-bold text-purple-700">₦{monthlyEarnings.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="ri-bar-chart-line text-purple-700 text-xl"></i>
              </div>
            </div>
          </div>
        )}

        {/* Purchases This Month (Buyer/Seller) */}
        {(userType === 'buyer' || userType === 'seller') && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Purchases This Month</p>
                <p className="text-2xl font-bold text-purple-700">₦{(walletData.transactions || []).filter(t => {
                  const ts: any = (t as any).createdAt;
                  const dt = ts && ts.seconds ? new Date(ts.seconds * 1000) : (t.date ? new Date(t.date) : null);
                  const now = new Date();
                  return dt && dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear() && (t.type === 'debit' || t.type === 'escrow_hold');
                }).reduce((sum, t) => sum + (t.amount || 0), 0).toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-purple-700 text-xl"></i>
              </div>
            </div>
          </div>
        )}
        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Transaction History</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {(walletData.transactions || []).filter(t => t.type !== 'platform_fee' && !String(t.description || '').match(/flutterwave fee|platform fee/i)).map((transaction) => (
              <div key={transaction.id} className="p-6 flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                    transaction.type === 'credit' ? 'bg-green-100' :
                    transaction.type === 'debit' ? 'bg-red-100' :
                    transaction.type === 'escrow_hold' ? 'bg-orange-100' :
                    'bg-blue-100'
                  }`}>
                    <i className={`text-lg ${
                      transaction.type === 'credit' || transaction.type === 'escrow_release' 
                        ? 'ri-add-line text-green-600' : 'ri-subtract-line text-red-600'
                    }`}></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{(transaction.description || '').replace(/flutterwave fee/ig, 'Platform fee')}</p>
                    <p className="text-sm text-gray-500">{transaction.date}</p>
                    <p className="text-sm text-gray-500">
                      {transaction.method ? `Method: ${transaction.method}` : 'Method: n/a'}
                      {transaction.reference ? ` • Ref: ${transaction.reference}` : ''}
                      {transaction.orderId ? ` • Order: ${transaction.orderId}` : ''}
                    </p>
                    {transaction.type === 'escrow_hold' && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">Purchase</span>
                    )}
                    {userType === 'seller' && transaction.type === 'escrow_release' && (
                      <span className="inline-block mt-1 ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Sale</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${
                    transaction.type === 'credit' || transaction.type === 'escrow_release' 
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.type === 'credit' || transaction.type === 'escrow_release' ? '+' : '-'}
                    ₦{transaction.amount.toLocaleString()}
                  </p>
                  <p className={`text-sm ${
                    transaction.status === 'completed' ? 'text-green-600' :
                    transaction.status === 'pending' || transaction.status === 'held' ? 'text-orange-600' :
                    'text-gray-600'
                  }`}>
                    {transaction.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fund Wallet Modal */}
      {showFundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Fund Wallet</h3>
              <button
                onClick={() => setShowFundModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₦)</label>
                <input
                  type="number"
                  value={fundAmount}
                  onChange={(e) => setFundAmount(e.target.value)}
                  placeholder="Enter amount (min ₦100)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {fundAmount && parseFloat(fundAmount) >= 100 && (
                  <p className="text-xs text-gray-500 mt-1">
                    You'll be charged <strong>₦{paystackChargeForNaira(parseFloat(fundAmount)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> (includes Paystack fee).{' '}
                    <strong>₦{parseFloat(fundAmount).toLocaleString()}</strong> will be credited to your wallet.
                  </p>
                )}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700 font-medium">Paying via Paystack — Card, Bank Transfer, USSD</p>
              </div>
              
              {fundError && (
                <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded-lg p-3">
                  {fundError}
                </div>
              )}
              <button
                onClick={handleFundWallet}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Proceed to Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-sm w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">Withdraw Funds</h3>
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₦)</label>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Min ₦1,000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Available: ₦{walletData.balance.toLocaleString()}</p>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                <input
                  type="text"
                  value={bankSearch}
                  onChange={(e) => {
                    setBankSearch(e.target.value);
                    setWithdrawBankCode('');
                    setResolvedAccountName('');
                    setResolveError(null);
                    setShowBankSuggestions(true);
                  }}
                  onFocus={() => setShowBankSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowBankSuggestions(false), 150)}
                  placeholder={banksLoading ? 'Loading banks...' : 'Type to search bank...'}
                  autoComplete="off"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${withdrawBankCode ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
                />
                {withdrawBankCode && (
                  <button
                    type="button"
                    onClick={() => { setBankSearch(''); setWithdrawBankCode(''); setResolvedAccountName(''); setResolveError(null); }}
                    className="absolute right-2 top-8 text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
                {showBankSuggestions && bankSearch.trim().length > 0 && filteredBanks.length > 0 && !withdrawBankCode && (
                  <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {filteredBanks.slice(0, 8).map((b) => (
                      <li
                        key={b.code}
                        onMouseDown={() => {
                          setWithdrawBankCode(b.code);
                          setBankSearch(b.name);
                          setShowBankSuggestions(false);
                          setResolvedAccountName('');
                          setResolveError(null);
                        }}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-800"
                      >
                        {b.name}
                      </li>
                    ))}
                  </ul>
                )}
                {banksError && <p className="text-xs text-red-600 mt-1">{banksError}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={withdrawBankAccountNumber}
                  onChange={(e) => { setWithdrawBankAccountNumber(e.target.value.replace(/\D/g, '')); setResolvedAccountName(''); setResolveError(null); }}
                  placeholder="10-digit account number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Account name auto-resolved */}
              <div className="min-h-[36px] flex items-center">
                {isResolvingAccount && (
                  <p className="text-sm text-gray-500 italic">Verifying account...</p>
                )}
                {!isResolvingAccount && resolvedAccountName && (
                  <div className="w-full px-3 py-2 bg-green-50 border border-green-300 rounded-lg">
                    <p className="text-xs text-green-600 font-medium">Account Name</p>
                    <p className="text-sm font-semibold text-green-800">{resolvedAccountName}</p>
                  </div>
                )}
                {!isResolvingAccount && resolveError && (
                  <p className="text-xs text-red-600">{resolveError}</p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  Withdrawals are processed via Paystack directly to your bank account.
                </p>
              </div>

              <button
                onClick={handleWithdraw}
                disabled={isResolvingAccount || !resolvedAccountName}
                className="w-full bg-green-600 text-white py-2.5 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResolvingAccount ? 'Verifying account...' : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN Setup Modal */}
      {showPinSetupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Set Withdrawal PIN</h3>
              <button onClick={() => setShowPinSetupModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Create a 6-digit PIN to authorize withdrawals.</p>
            <div className="space-y-4">
              <input
                type="password"
                value={pinSetup}
                onChange={(e) => setPinSetup(e.target.value)}
                placeholder="Enter 6-digit PIN"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="password"
                value={pinSetupConfirm}
                onChange={(e) => setPinSetupConfirm(e.target.value)}
                placeholder="Confirm PIN"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {pinStatus && <p className="text-sm text-red-600">{pinStatus}</p>}
              <button
                onClick={submitPinSetup}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save PIN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN Enter Modal */}
      {showPinEnterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Enter Withdrawal PIN</h3>
              <button onClick={() => setShowPinEnterModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Enter your 6-digit PIN to authorize withdrawal.</p>
            <div className="space-y-4">
              <input
                type="password"
                value={pinEntry}
                onChange={(e) => setPinEntry(e.target.value)}
                placeholder="Enter 6-digit PIN"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {pinStatus && <p className="text-sm text-red-600">{pinStatus}</p>}
              <button
                onClick={submitPinEnter}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                Confirm and Withdraw
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Deposit Success Modal */}
      {showDepositSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-4xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Deposit Successful!</h3>
            <p className="text-3xl font-bold text-green-600 my-3">₦{depositSuccessAmount.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mb-6">Your wallet has been credited. Your new balance is reflected above.</p>
            <button
              onClick={() => setShowDepositSuccess(false)}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Deposit Failed Modal */}
      {showDepositFailed && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-close-circle-fill text-4xl text-red-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Deposit Failed</h3>
            <p className="text-sm text-gray-600 mb-6">{depositFailedReason}</p>
            <button
              onClick={() => { setShowDepositFailed(false); setShowFundModal(true); }}
              className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors mb-2"
            >
              Try Again
            </button>
            <button
              onClick={() => setShowDepositFailed(false)}
              className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Withdrawal Success Modal */}
      {showWithdrawSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-bank-fill text-4xl text-blue-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Withdrawal Submitted!</h3>
            <p className="text-3xl font-bold text-blue-600 my-3">₦{withdrawSuccessAmount.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mb-2">Your transfer is being processed via Paystack and is currently <strong>pending</strong>.</p>
            <p className="text-sm text-gray-500 mb-6">Funds typically arrive within a few minutes to a few hours. You will see a status update in your transaction history.</p>
            <button
              onClick={() => setShowWithdrawSuccess(false)}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
      {/* Withdrawal Failed Modal */}
      {showWithdrawFailed && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-close-circle-fill text-4xl text-red-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Withdrawal Failed</h3>
            <p className="text-sm text-gray-600 mb-4">{withdrawFailedReason}</p>

            {withdrawFailedRaw && withdrawFailedRaw !== withdrawFailedReason && (
              <div className="mb-4 text-left">
                <button
                  type="button"
                  onClick={() => setShowWithdrawTechDetails(s => !s)}
                  className="text-xs text-gray-500 hover:text-gray-700 underline w-full text-center"
                >
                  {showWithdrawTechDetails ? 'Hide technical details' : 'Show technical details'}
                </button>
                {showWithdrawTechDetails && (
                  <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Raw error from server</span>
                      <button
                        type="button"
                        onClick={() => { try { navigator.clipboard?.writeText(withdrawFailedRaw); } catch {} }}
                        className="text-[10px] text-blue-600 hover:text-blue-700 underline"
                      >
                        Copy
                      </button>
                    </div>
                    <pre className="text-[11px] text-gray-700 whitespace-pre-wrap break-words font-mono leading-snug">{withdrawFailedRaw}</pre>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => { setShowWithdrawFailed(false); setShowWithdrawModal(true); }}
              className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors mb-2"
            >
              Try Again
            </button>
            <button
              onClick={() => setShowWithdrawFailed(false)}
              className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletPage;

// Paystack (NGN) fee model: 1.5% + ₦100, with the ₦100 flat fee waived for charges
// under ₦2,500 and the total fee capped at ₦2,000. Given the amount the user wants
// credited to their wallet (net), this returns the amount to actually charge (gross)
// so that after Paystack deducts its fee, the platform still nets the full amount.
const paystackChargeForNaira = (net: number): number => {
  const DECIMAL = 0.015;
  const FLAT = 100;
  const CAP = 2000;
  const THRESHOLD = 2500;
  if (!Number.isFinite(net) || net <= 0) return net;
  let gross = net / (1 - DECIMAL);
  // The ₦100 flat fee only applies once the charged amount reaches ₦2,500.
  if (gross >= THRESHOLD) gross = (net + FLAT) / (1 - DECIMAL);
  // Paystack caps its total fee at ₦2,000.
  if (gross - net > CAP) gross = net + CAP;
  // Round up to the nearest kobo so rounding never leaves the platform short.
  return Math.ceil(gross * 100) / 100;
};

// Safe converter: supports Firestore Timestamp, epoch ms, and ISO strings
const toDateSafe = (val: any): Date | null => {
  try {
    if (!val) return null;
    if (typeof val.toDate === 'function') return val.toDate();
    if (typeof val === 'number') return new Date(val);
    if (typeof val === 'string') {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  } catch {
    return null;
  }
};
